/**
 * The one enrichment page, for all nine entities (MOO-4803, design C.6/C.7).
 *
 * It owns everything the editor must not: the load, the two baselines, the
 * save order, and the bottom bar. The save is the part worth reading twice —
 * translations go out FIRST and a failure there aborts the enrich PUT, because
 * the eight non-genre handlers re-read the translations map inside their own
 * transaction, and a merge that lands after that re-read is a merge the entity
 * does not have.
 *
 * `draft` is the single source of truth for what gets saved. The editor pushes
 * the raw react-hook-form values into it on every change, so a text input the
 * curator EMPTIED arrives as `""` rather than vanishing — which is the only
 * thing that tells `buildEnrichBody` to clear the stored value instead of
 * leaving it alone.
 */
// React 19 types no longer declare a global `JSX` namespace, so the return
// type is imported from the package that owns it — the same import the two
// section components use.
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router";

import { useNotification } from "@refinedev/core";
import { ArrowLeft, CheckCircle, ChevronDown, Sparkles } from "lucide-react";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { EnrichmentStatusBadge } from "@/components/common/enrichment-status-badge";
import type { AreaScope } from "@/components/enrichment/area-enrichment-field";
import { EnrichmentEditor } from "@/components/enrichment/enrichment-editor";
import { JsonEnrichmentEditor } from "@/components/enrichment/json-enrichment-editor";
import { TranslationsSection } from "@/components/enrichment/translations-section";
import { TransliterationsSection } from "@/components/enrichment/transliterations-section";
import { CuratedFieldsProvider } from "@/components/field-curation/curated-fields-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { cn, withClearedFields } from "@/lib/utils";
import { ArtistEnrichmentTrigger } from "@/pages/artists/components/artist-enrichment-trigger";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import { VenueEnrichmentTrigger } from "@/pages/venues/components/venue-enrichment-trigger";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

import { buildEnrichmentForm } from "./build-schema";
import {
  type EnrichmentPageConfig,
  enrichmentConfigFor,
  enrichmentOf,
  needsEnrichment,
} from "./config";
import { isPlainObject } from "./field-rules";
import { buildEnrichBody, buildTranslationsBody } from "./save-bodies";
import type { TranslationMap } from "./translations";
import { useEnrichmentRepository } from "./use-enrichment-repository";

/** A no-op with a stable identity, for the raw editor's validity report. */
const noop = () => {};

// ---------------------------------------------------------------------------
// Draft bookkeeping
// ---------------------------------------------------------------------------

/** A JSON-shaped deep clone — the baselines must not share structure. */
function cloneJson<T>(value: T): T {
  if (Array.isArray(value)) return value.map(cloneJson) as unknown as T;
  if (isPlainObject(value)) {
    const copy: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value))
      copy[key] = cloneJson(item);
    return copy as unknown as T;
  }
  return value;
}

/**
 * Deep equality, order-insensitively and with `null`/`undefined` treated as the
 * same "nothing". Both halves of the comparison come from the same loaded
 * document, so a key-order difference is never a real edit — and a `null` the
 * API stored where the form holds `undefined` must not fake one either.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil || bNil) return aNil && bNil;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const aKeys = Object.keys(a).filter((key) => a[key] !== undefined);
    const bKeys = Object.keys(b).filter((key) => b[key] !== undefined);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => deepEqual(a[key], b[key]));
  }

  // Two scalars — the case every leaf of the comparison ends on. Without this
  // line the recursion bottoms out at `false`, so two identical strings read as
  // an edit and every key of the draft counts as dirty.
  return a === b;
}

/**
 * The keys of `draft` that differ from `baseline` — the `changed` half of
 * `buildEnrichBody`'s input.
 *
 * A key the draft does not carry at all is left out on purpose: the enrich body
 * is built from the LOADED object with these keys merged over it, so an absent
 * key keeps its stored value. `undefined` is never emitted either — the PUT is
 * a full replace, and a key JSON drops is a key the API clears.
 */
function dirtyOnly(
  draft: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(draft)) {
    if (value === undefined) continue;
    if (deepEqual(value, baseline[key])) continue;
    changed[key] = value;
  }
  return changed;
}

/** The city a venue's `area` is picked from: its `geodata` name and country code. */
function areaScopeOf(record: Record<string, unknown>): AreaScope | undefined {
  const geodata = record.geodata;
  if (typeof geodata !== "object" || geodata === null) return undefined;
  const { name, countryCode } = geodata as Record<string, unknown>;
  return {
    city: typeof name === "string" ? name : undefined,
    countryCode: typeof countryCode === "string" ? countryCode : undefined,
  };
}

/** The lifecycle status, which sits at the document root on all nine. */
function statusOf(record: Record<string, unknown>): string | undefined {
  if (typeof record.enrichmentStatus === "string")
    return record.enrichmentStatus;
  const nested = record.enrichment;
  if (isPlainObject(nested) && typeof nested.enrichmentStatus === "string") {
    return nested.enrichmentStatus;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// The D6 enrich triggers
// ---------------------------------------------------------------------------

/**
 * One of the four "run enrichment" affordances, picked by resource.
 *
 * Each branch is its own component on purpose: the repository hooks behind them
 * are resource-specific, and only the mounted one may run.
 */
function EnrichTrigger({
  config,
  id,
  name,
  onTriggered,
}: {
  config: EnrichmentPageConfig;
  id: string;
  name: string;
  onTriggered: () => void;
}) {
  switch (config.key) {
    case "artists":
      return <ArtistTrigger id={id} name={name} onTriggered={onTriggered} />;
    case "venues":
      return <VenueTrigger id={id} name={name} onTriggered={onTriggered} />;
    case "events":
      return <EventTrigger id={id} onTriggered={onTriggered} />;
    case "geodata":
      return <GeodataTrigger id={id} onTriggered={onTriggered} />;
    default:
      return null;
  }
}

function ArtistTrigger({
  id,
  name,
  onTriggered,
}: {
  id: string;
  name: string;
  onTriggered: () => void;
}) {
  const { triggerEnrich } = useArtistRepository();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (params: { instagramScreenshot?: string }) => {
    setIsSubmitting(true);
    const success = await triggerEnrich(id, params);
    setIsSubmitting(false);
    if (success) onTriggered();
  };

  return (
    <ArtistEnrichmentTrigger
      artistName={name}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
    />
  );
}

function VenueTrigger({
  id,
  name,
  onTriggered,
}: {
  id: string;
  name: string;
  onTriggered: () => void;
}) {
  const { triggerEnrich, searchPlace } = useVenueRepository();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (params: {
    instagramPageUrl: string;
    instagramScreenshot?: string;
    place?: unknown;
  }) => {
    setIsSubmitting(true);
    const success = await triggerEnrich(id, params);
    setIsSubmitting(false);
    if (success) onTriggered();
  };

  return (
    <VenueEnrichmentTrigger
      venueName={name}
      isSubmitting={isSubmitting}
      searchPlace={searchPlace}
      onSubmit={handleSubmit}
    />
  );
}

/** The plain card the event and geodata pages already use for their run button. */
function TriggerCard({
  title,
  description,
  label,
  isEnriching,
  onRun,
}: {
  title: string;
  description: string;
  label: string;
  isEnriching: boolean;
  onRun: () => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        <Button onClick={onRun} disabled={isEnriching} className="gap-2">
          <Sparkles className="h-4 w-4" />
          {isEnriching ? "Enriching..." : label}
        </Button>
      </CardContent>
    </Card>
  );
}

function EventTrigger({
  id,
  onTriggered,
}: {
  id: string;
  onTriggered: () => void;
}) {
  const { triggerEnrich } = useEventRepository();
  const [isEnriching, setIsEnriching] = useState(false);

  const run = async () => {
    setIsEnriching(true);
    const success = await triggerEnrich(id);
    setIsEnriching(false);
    if (success) onTriggered();
  };

  return (
    <TriggerCard
      title="Trigger Enrichment"
      description="Run AI enrichment to generate features, keywords, FAQ and transliterations for this event. This computes 23 event features from the venue, lineup artists and genres."
      label="Trigger Enrichment"
      isEnriching={isEnriching}
      onRun={run}
    />
  );
}

function GeodataTrigger({
  id,
  onTriggered,
}: {
  id: string;
  onTriggered: () => void;
}) {
  const { triggerEnrich } = useGeodataRepository();
  const [isEnriching, setIsEnriching] = useState(false);

  const run = async () => {
    setIsEnriching(true);
    const success = await triggerEnrich(id);
    setIsEnriching(false);
    if (success) onTriggered();
  };

  return (
    <TriggerCard
      title="Trigger Enrichment"
      description="Run AI enrichment to generate the about text, aliases and metadata for this geodata row."
      label="Run enrichment"
      isEnriching={isEnriching}
      onRun={run}
    />
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function EnrichmentPage({
  resource,
}: {
  resource: string;
}): JSX.Element {
  const config = useMemo(() => enrichmentConfigFor(resource), [resource]);
  const { id } = useParams();
  const repo = useEnrichmentRepository(resource);
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();

  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [storedTranslations, setStoredTranslations] = useState<TranslationMap>(
    {},
  );
  /** Bumped on every load, so the editor remounts on the fresh baselines. */
  const [generation, setGeneration] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  // The repository's functions are rebuilt every render, and Refine's `open` /
  // `setLoading` identities are not ours to rely on. Refs keep the load effect
  // keyed on the id alone.
  const repoRef = useRef(repo);
  repoRef.current = repo;
  const notifyRef = useRef(open);
  notifyRef.current = open;
  const loadingRef = useRef(setLoading);
  loadingRef.current = setLoading;

  const seed = useCallback(
    (data: Record<string, unknown>) => {
      const loaded = enrichmentOf(config, data);
      setRecord(data);
      setBaseline(cloneJson(loaded));
      setDraft(cloneJson(loaded));
      setStoredTranslations(
        cloneJson((loaded.translations ?? {}) as TranslationMap),
      );
      setGeneration((current) => current + 1);
    },
    [config],
  );

  const load = useCallback(
    async (entityId: string) => {
      const data = await repoRef.current.get(entityId);
      seed(data);
      return data;
    },
    [seed],
  );

  useEffect(() => {
    if (!id) return;
    let active = true;
    loadingRef.current(true);
    load(id)
      .catch((error) => {
        if (!active) return;
        notifyRef.current?.({
          type: "error",
          message: `Failed to load ${config.noun.toLowerCase()}`,
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => {
        if (active) loadingRef.current(false);
      });
    return () => {
      active = false;
    };
  }, [id, load, config.noun]);

  const curatedFields =
    resource === "artists"
      ? ((record?.curatedFields as string[] | null | undefined) ?? null)
      : null;

  const handleApprove = async () => {
    if (!id || !record) return;

    const bundle = buildEnrichmentForm(config, baseline);

    // C.6 step 1 — the STRICT half of the read/write split. Validating does not
    // strip the pipeline-owned keys the read carried in: the schema passes
    // everything it does not model straight through.
    const submit = bundle.schema.safeParse(draft);
    if (!submit.success) {
      notifyRef.current?.({
        type: "error",
        message: "Enrichment is not valid to save",
        description: submit.error.issues
          .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
          .join("; "),
      });
      return;
    }

    // C.6 step 2 — an input the curator emptied is already `""` here (the
    // editor pushes raw form values), so this is the recovery for the values
    // that arrive through a real form submit, where `removeEmptyValues` has
    // deleted them. `""` is only ever emitted for the config's own allowlist.
    const cleared: Record<string, unknown> = {
      ...draft,
      ...withClearedFields(draft, draft, baseline, bundle.clearableKeys),
    };

    // C.6 steps 3-4. Translations are diffed per locale and never ride the
    // enrich body — the handlers re-read the stored map themselves.
    const changed = dirtyOnly(cleared, baseline);
    delete changed.translations;
    const enrichDirty = Object.keys(changed).length > 0;
    const translationsBody = buildTranslationsBody(
      id,
      storedTranslations,
      cleared.translations as TranslationMap,
    );

    if (!enrichDirty && !translationsBody) {
      notifyRef.current?.({ type: "success", message: "Nothing to save." });
      return;
    }

    setIsSaving(true);
    try {
      // C.6 step 5 — translations first, and a failure ABORTS the rest.
      if (translationsBody) {
        const saved = await repoRef.current.saveTranslations(translationsBody);
        if (!saved) return;
      }

      // C.6 step 6.
      if (enrichDirty) {
        const body = buildEnrichBody(config, {
          id,
          loaded: baseline,
          changed,
          rawValues: cleared,
          clearableKeys: bundle.clearableKeys,
        });
        const saved = await repoRef.current.saveEnrichment(body);
        if (!saved) return;
      }

      // C.6 step 8 — reload in place and re-seed both baselines. The page
      // deliberately does not navigate away: editing many fields is the point,
      // and the back link is one click.
      await load(id);
      notifyRef.current?.({
        type: "success",
        message: "Enrichment approved",
        description: "The enrichment data has been saved successfully.",
      });
    } catch (error) {
      notifyRef.current?.({
        type: "error",
        message: `Failed to load ${config.noun.toLowerCase()}`,
        description: toHumanErrorMessage(error),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const name = record ? (config.nameOf(record) ?? "") : "";
  const areaScope = useMemo(
    () => (record ? areaScopeOf(record) : undefined),
    [record],
  );
  const status = record ? statusOf(record) : undefined;

  return (
    <div className="p-8 space-y-6 max-w-4xl mx-auto pb-20">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={id ? config.backPath(id) : "/"}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {config.noun}
          </Link>
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div>
          <h1 className="text-lg font-semibold leading-tight">{name}</h1>
          <p className="text-xs text-muted-foreground">Enrichment Review</p>
        </div>
        <EnrichmentStatusBadge status={status} />
      </div>

      {/* Gated on `record`: the status and the entity name both come from it,
          and `needsEnrichment(undefined)` is true — so an ungated card would
          flash a nameless trigger on every load, failed loads included. */}
      {record && id && config.hasEnrichTrigger && needsEnrichment(status) && (
        <EnrichTrigger
          config={config}
          id={id}
          name={name}
          onTriggered={() => void load(id)}
        />
      )}

      {record && (
        <>
          <CuratedFieldsProvider curated={curatedFields} prefix="enrichment.">
            <EnrichmentEditor
              key={`${resource}-${generation}`}
              config={config}
              enrichment={draft}
              updateEnrichment={setDraft}
              disabled={isSaving}
              areaScope={areaScope}
            />
          </CuratedFieldsProvider>

          {config.hasTransliterations && (
            <TransliterationsSection
              value={
                draft.transliterations as Record<string, string[]> | undefined
              }
              onChange={(next) =>
                setDraft((current) => ({ ...current, transliterations: next }))
              }
              disabled={isSaving}
            />
          )}

          <TranslationsSection
            value={(draft.translations ?? {}) as TranslationMap}
            stored={storedTranslations}
            onChange={(next) =>
              setDraft((current) => ({ ...current, translations: next }))
            }
            disabled={isSaving}
          />

          {/* C.7's last row: the escape hatch for a shape the editor cannot
              model, and the drift fallback when a probe is off-contract. */}
          <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Raw JSON</span>
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transition-transform text-muted-foreground",
                        rawOpen && "rotate-180",
                      )}
                    />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    The whole enrichment object, editable as JSON. Use it when a
                    value above has no inline editor.
                  </p>
                  <JsonEnrichmentEditor
                    value={draft}
                    onChange={setDraft}
                    onValidityChange={noop}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </>
      )}

      <BottomActionBar className="justify-end gap-2">
        <Button onClick={handleApprove} disabled={!record || isSaving}>
          <CheckCircle className="h-4 w-4 mr-2" />
          Approve & save
        </Button>
      </BottomActionBar>
    </div>
  );
}

export default EnrichmentPage;
